<template>
  <PayOpsModalShell
    :visible="visible"
    title="Bulk Job Progress"
    subtitle="Live processing status for the selected orders"
    icon="fa-tasks"
    :close-disabled="isRetrying"
    @close="$emit('close')"
  >
    <div v-if="isLoading && !job" class="text-center py-4">
      <div class="spinner-border text-primary"></div>
      <p class="text-muted mt-2">Loading job status...</p>
    </div>
    <template v-else-if="job">
      <div class="mb-3">
        <div class="d-flex justify-content-between mb-1">
          <span class="fw-bold">{{ job.type || job.action }}</span>
          <span class="badge" :class="statusBadgeClass">{{ job.status }}</span>
        </div>
        <div class="progress progress-tall">
          <div class="progress-bar bg-success" :style="{ width: successPct + '%' }">
            {{ job.successCount }}
          </div>
          <div class="progress-bar bg-danger" :style="{ width: failurePct + '%' }">
            {{ job.failureCount }}
          </div>
          <div class="progress-bar bg-secondary" :style="{ width: pendingPct + '%' }">
            {{ pendingCount }}
          </div>
        </div>
        <div class="small text-muted mt-1">
          {{ job.successCount }} success / {{ job.failureCount }} failed /
          {{ pendingCount }} pending of {{ job.totalItems }} total
        </div>
      </div>

      <dl class="row small mb-3">
        <dt class="col-sm-4">Job ID</dt>
        <dd class="col-sm-8 font-monospace">{{ job.jobId }}</dd>
        <dt class="col-sm-4">Actor</dt>
        <dd class="col-sm-8">{{ actorDisplay }}</dd>
        <dt class="col-sm-4">Reason</dt>
        <dd class="col-sm-8">{{ job.reason || '—' }}</dd>
        <dt class="col-sm-4">Created</dt>
        <dd class="col-sm-8">
          <ZonedDateTime :value="job.createdAt" :zone="userTimezone" kind="user" />
        </dd>
        <dt v-if="job.completedAt" class="col-sm-4">Completed</dt>
        <dd v-if="job.completedAt" class="col-sm-8">
          <ZonedDateTime :value="job.completedAt" :zone="userTimezone" kind="user" />
        </dd>
      </dl>

      <div v-if="job.notification" class="alert alert-info outline small mb-3">
        <i class="fa fa-bell me-1"></i>{{ job.notification.summary }}
      </div>

      <div class="bulk-items-scroll">
        <table class="table table-sm table-striped mb-0">
          <thead class="table-light sticky-top">
            <tr>
              <th>Order UUID</th>
              <th>Status</th>
              <th>Error</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, idx) in job.items" :key="idx">
              <td class="font-monospace small">
                <router-link
                  :to="{
                    name: 'pay-operations-order-amendment-detail',
                    params: { uuid: item.orderUuid },
                  }"
                >
                  {{ shortUuid(item.orderUuid) }}
                </router-link>
              </td>
              <td>
                <span class="badge" :class="itemBadgeClass(item.status)">{{ item.status }}</span>
              </td>
              <td class="small text-danger">{{ item.error || '—' }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="text-end mt-3">
        <router-link
          :to="{ name: 'pay-operations-bulk-job-detail', params: { id: job.jobId } }"
          class="small"
        >
          Open full bulk job detail page <i class="fa fa-external-link"></i>
        </router-link>
      </div>
    </template>
    <template #footer>
      <button
        v-if="job && job.failureCount > 0 && canRetry"
        class="btn btn-warning btn-sm"
        :disabled="isRetrying"
        @click="retryFailures"
      >
        <span v-if="isRetrying">
          <span class="spinner-border spinner-border-sm me-1"></span>Retrying...
        </span>
        <span v-else><i class="fa fa-refresh me-1"></i>Retry Failures</span>
      </button>
      <button class="btn btn-outline-secondary" @click="$emit('close')">Close</button>
    </template>
  </PayOpsModalShell>
</template>

<script setup>
import { ref, computed, watch, onBeforeUnmount } from 'vue';
import { useToast } from 'vue-toastification';
import { apiClient, API_PATHS } from '@/services/api';
import { shortIdentifier as shortUuid } from '@/utils/display-formatters';
import PayOpsModalShell from '../shared/PayOpsModalShell.vue';
import ZonedDateTime from '@/components/common/ZonedDateTime.vue';
import { getUserAddDataFromLocalStorage } from '@/utils/userUtils';
import { resolveUserTimezone } from '@/utils/timezoneDisplay';

const props = defineProps({
  visible: { type: Boolean, default: false },
  jobId: { type: String, default: '' },
  canRetry: { type: Boolean, default: false },
});

defineEmits(['close']);

const toast = useToast();
const userTimezone = resolveUserTimezone((getUserAddDataFromLocalStorage() || {}).timezone);
const job = ref(null);
const isLoading = ref(false);
const isRetrying = ref(false);
let pollTimer = null;

const pendingCount = computed(() => {
  if (!job.value) return 0;
  return (
    job.value.totalItems -
    (job.value.successCount ?? 0) -
    (job.value.failureCount ?? 0) -
    (job.value.skipCount ?? 0)
  );
});
const successPct = computed(() =>
  job.value ? ((job.value.successCount ?? 0) / job.value.totalItems) * 100 : 0,
);
const failurePct = computed(() =>
  job.value ? ((job.value.failureCount ?? 0) / job.value.totalItems) * 100 : 0,
);
const pendingPct = computed(() =>
  job.value ? (pendingCount.value / job.value.totalItems) * 100 : 0,
);

const statusBadgeClass = computed(() => {
  switch (job.value?.status) {
    case 'completed':
      return 'bg-success';
    case 'partial':
      return 'bg-warning text-dark';
    case 'failed':
      return 'bg-danger';
    case 'running':
      return 'bg-info';
    default:
      return 'bg-secondary';
  }
});

const actorDisplay = computed(() => {
  const actor = job.value?.actor;
  if (!actor) return '—';
  if (typeof actor === 'string') return actor;
  return actor.displayName || actor.email || actor.uid || '—';
});

function itemBadgeClass(status) {
  switch (status) {
    case 'success':
      return 'bg-success';
    case 'failure':
      return 'bg-danger';
    case 'skipped':
      return 'bg-secondary';
    default:
      return 'bg-light text-dark';
  }
}

async function fetchJob() {
  if (!props.jobId) return;
  isLoading.value = true;
  try {
    const { data } = await apiClient.rpIntegration.get(
      API_PATHS.rpIntegration.payOperations.orderAmendments.bulkJobById(props.jobId),
    );
    job.value = data;
    if (['completed', 'partial', 'failed'].includes(job.value?.status)) stopPolling();
  } catch {
    toast.error('Failed to fetch bulk job status.');
  } finally {
    isLoading.value = false;
  }
}

function startPolling() {
  stopPolling();
  fetchJob();
  pollTimer = setInterval(fetchJob, 3000);
}

function stopPolling() {
  if (pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
}

async function retryFailures() {
  isRetrying.value = true;
  try {
    await apiClient.rpIntegration.post(
      API_PATHS.rpIntegration.payOperations.orderAmendments.bulkJobRetry(props.jobId),
    );
    toast.success('Retry initiated for failed items.');
    startPolling();
  } catch (err) {
    toast.error(err?.response?.data?.message ?? 'Retry failed.');
  } finally {
    isRetrying.value = false;
  }
}

watch(
  () => props.visible,
  (v) => {
    if (v && props.jobId) startPolling();
    else stopPolling();
  },
);

onBeforeUnmount(stopPolling);
</script>

<style scoped>
.progress-tall {
  height: 24px;
}
.bulk-items-scroll {
  max-height: 300px;
  overflow-y: auto;
}
</style>
